import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextInput from 'react/elements/TextInput';
import Button from 'react/components/Button';
import MaterialIcon from 'react/elements/MaterialIcon';
import searchMealIdeas, { findIdeaNamed } from 'react/components/calendar/searchMealIdeas';
import { addMealIdeaToGroup, createMealIdea, MEAL_IDEAS_LOADED } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex } from '../../../redux/food/selectors';
import useOffline, { NEEDS_CONNECTION_TITLE, onlineOnly } from '../../hooks/useOffline';

// Fewer letters than this match nearly everything, which is noise rather than a hint.
const SIMILAR_FROM_LENGTH = 3;
const SIMILAR_LIMIT = 3;

/**
 * Enter adds an idea to the group (or to no group), and the field clears once it exists.
 *
 * `enterkeyhint` is what makes that work on Android: with another text field further down the page
 * the keyboard offers "Next" instead, which moves focus without sending Enter. React 16 does not
 * know the attribute, so it is spelled in lowercase to pass through untouched. The Add button, shown
 * once there is something to add, covers any keyboard that ignores it.
 *
 * While a name is typed, ideas with a similar one are listed under the field, so an idea is found
 * rather than made twice. A name another idea already has, ignoring case and spacing, cannot be
 * added at all; the server refuses it too. In a group, tapping a listed idea puts that one in the
 * group instead.
 *
 * Both are online-only, so offline the field is disabled, titled "Needs a connection".
 */
export default function NewMealIdeaInput({ groupId = null }) {
	const dispatch = useDispatch();
	const [name, setName] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const offline = useOffline();
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const memberIds = useSelector((state) => {
		const group = groupId === null ? null : selectMealIdeasIndex(state).groupsById[groupId];
		return group ? group.meal_idea_ids : null;
	});
	const ideas = useMemo(() => Object.values(ideasById), [ideasById]);

	const trimmed = name.trim();
	const taken = findIdeaNamed(ideas, trimmed);
	const similar = useMemo(() => {
		if(taken) {
			return [taken];
		}
		return trimmed.length >= SIMILAR_FROM_LENGTH ? searchMealIdeas(ideas, trimmed).slice(0, SIMILAR_LIMIT) : [];
	}, [ideas, trimmed, taken]);
	const inThisGroup = (idea) => !!memberIds && memberIds.includes(idea.id);

	const addExisting = (idea) => {
		dispatch(addMealIdeaToGroup(idea.id, groupId));
		setName('');
	};

	const submit = () => {
		if(!trimmed || submitting || taken) {
			return;
		}
		setSubmitting(true);
		dispatch(createMealIdea(trimmed, groupId)).then((action) => {
			setSubmitting(false);
			if(action && action.type === MEAL_IDEAS_LOADED) {
				setName('');
			}
		});
	};

	return (
		<React.Fragment>
			<div className='new_meal_idea'>
				<MaterialIcon className='new_meal_idea__icon'>add</MaterialIcon>
				<div className='input-field'>
					<TextInput
						label='New meal idea'
						enterkeyhint='done'
						{...onlineOnly(offline)}
						value={name}
						onChange={(event) => setName(event.target.value)}
						onKeyDown={(event) => {
							if(event.key === 'Enter') {
								event.preventDefault();
								submit();
							}
						}}
					/>
				</div>
				{!!trimmed && <Button small onClick={submit} disabled={submitting || !!taken} {...onlineOnly(offline)}>Add</Button>}
			</div>
			{similar.length > 0 &&
			<div className={`new_meal_idea__similar${taken ? ' is-taken' : ''}`}>
				<span className='new_meal_idea__similar-label'>{taken ? 'Already a meal idea:' : 'Similar:'}</span>
				{similar.map((idea) => (memberIds && !inThisGroup(idea) ?
					<button
						key={idea.id}
						type='button'
						className='btn-flat new_meal_idea__similar-idea is-addable'
						title={offline ? NEEDS_CONNECTION_TITLE : 'Add this one to the group'}
						disabled={offline}
						onClick={() => addExisting(idea)}
					>
						{idea.name}
						<MaterialIcon>add</MaterialIcon>
					</button> :
					<span key={idea.id} className='new_meal_idea__similar-idea'>
						{idea.name}
						{inThisGroup(idea) && <small> (in this group)</small>}
					</span>
				))}
			</div>
			}
		</React.Fragment>
	);
}
